import axios from 'axios';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction
} from 'react';
import { UserContext } from './authContext';
import { useAlert } from './alertContext';
import { documentTableItem } from '../lib/models/interfaces';
import { useNavigate } from 'react-router-dom';

export interface SearchHistory {
  _id: string;
  originalQuestion: string;
  sub: string;
  requestId: string;
  customerId: string;
  documentIds: string[] | documentTableItem[];
  searchKB: boolean;
  kbType: string[];
  createdAt: string;
  updatedAt: string;
  shared: string[];
  Documents: {
    _id: string;
    documentFileName: string;
    project: string;
    folder: string;
  }[];

  timeTakenInMS?: number;
  __v: number;
}

interface SearchHistoryContextType {
  history: SearchHistory[];
  setHistory: Dispatch<SetStateAction<SearchHistory[]>>;
  deleteById: (arg: string) => void;
  sharedHistory: SearchHistory[];
  setSharedHistory: Dispatch<SetStateAction<SearchHistory[]>>;
}

const SearchHistoryContext = createContext<
  SearchHistoryContextType | undefined
>(undefined);

export const useSearchHistory = (): SearchHistoryContextType => {
  const context = useContext(SearchHistoryContext);
  if (!context) {
    throw new Error(
      'useSearchHistory must be used within a SearchHistoryProvider'
    );
  }
  return context;
};

export const SearchHistoryProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const { token } = useContext(UserContext);
  const alert = useAlert();
  const navigate = useNavigate();
  const [history, setHistory] = useState<SearchHistory[]>([]);
  const [sharedHistory, setSharedHistory] = useState<SearchHistory[]>([]);

  const getAllHistories = async () => {
    if (!token) return;
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/searchHistory?type=all`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (JSON.stringify(result.data.result) !== JSON.stringify(history)) {
        console.log('results not equal');
        setHistory(result.data.result);
      }
      const sharedResult = await axios.post(
        `${process.env.REACT_APP_KORU_API}/searchHistory?type=shared`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (
        JSON.stringify(sharedResult.data.result) !==
        JSON.stringify(sharedHistory)
      ) {
        // console.log('results not equal');
        setSharedHistory(sharedResult.data.result);
      }
    } catch (e) {
      const error = e as Error;
      console.log('error', error);
      alert.handleError(e);
      // throw new Error(error.message);
    }
  };

  const deleteById = async (_id: string) => {
    if (!token) return;
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_KORU_API}/searchHistory?type=delete`,
        { _id },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      // console.log('result from delete', result.data);
      const temp = history.filter((hist) => hist._id !== _id);
      setHistory(temp);
      navigate('/app/landing');
    } catch (e) {
      const error = e as Error;
      console.log('error', error);
      alert.handleError(e);
      // throw new Error(error.message);
    }
  };

  useEffect(() => {
    if (!token) return;
    getAllHistories();
    // const intervalId = setInterval(() => {
    //   getAllHistories();
    // }, 15000);
    // return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <SearchHistoryContext.Provider
      value={{
        history,
        setHistory,
        deleteById,
        sharedHistory,
        setSharedHistory
      }}
    >
      {children}
    </SearchHistoryContext.Provider>
  );
};
